import {
  DELETE_FARM_API_URL,
  GET_ACTIVE_FARMS_BY_ORG_API_URL,
  UPSERT_FARM_API_URL,
} from 'constants/apiConstants';
import { IFarm, IFarmWriteResponse } from 'interfaces/IFarm';
import { IZone } from 'interfaces/IZone';
import axiosInstance from 'services/AxiosInstance';
import { resolveApiPath } from 'utils/UtilityMethods';

export const FarmService = {
  /**
   * Fetches all farms for a specific organization.
   *
   * Reads GetByOrgActive, so soft-deleted farms and their paddocks are
   * excluded server-side — the client does no filtering of its own.
   *
   * @param orgId - The organization ID to fetch farms for
   * @returns Promise containing an array of active farms and their paddocks
   */
  GetByOrgActive: async (orgId: string): Promise<IFarm[]> => {
    const response = await axiosInstance.get<IFarm[]>(
      `${GET_ACTIVE_FARMS_BY_ORG_API_URL}/${orgId}`,
      { _silentErrors: true }
    );
    return response.data;
  },

  /**
   * Creates or updates a farm. The whole farm is sent, paddocks included, so
   * callers build it from the latest cached farm (OP-2481). `zones` saves
   * changed paddock zones in the same call (confirmed by the lead
   * developer); the last save wins, with no version check.
   */
  Upsert: async (
    farm: IFarm,
    zones: IZone[] = []
  ): Promise<IFarmWriteResponse> => {
    const response = await axiosInstance.post<IFarmWriteResponse>(
      UPSERT_FARM_API_URL,
      { farm, zones },
      { headers: { ClientNow: new Date().toISOString() } }
    );
    return response.data;
  },

  /** Soft-deletes a farm; GetByOrgActive stops returning it and its paddocks. */
  Delete: async (farmId: string): Promise<IFarmWriteResponse> => {
    const response = await axiosInstance.delete<IFarmWriteResponse>(
      resolveApiPath(DELETE_FARM_API_URL, { id: farmId }),
      { headers: { ClientNow: new Date().toISOString() } }
    );
    return response.data;
  },
};

export default FarmService;
